import {
    handleAvailable,
    handleRequest,
    handleUnavailable,
} from '../reducerHandlers';
import { createReducer } from './utils';

import {
    ACHIEVEMENTS_AVAILABLE,
    ACHIEVEMENTS_REQUEST,
    ACHIEVEMENTS_UNAVAILABLE,
    SNACK_SET
} from './constants';

const defaultState = {
    // All achievements
    allAchievements: [],
    isAllAchievementsLoading: false,

    // General progress
    achievementsProgress: [],
    isAchievementsProgressLoading: false,

    // Achievement progress posting
    achievementProgressPostingMessage: null,
    isAchievementsProgressPosting: false,

    // Achievement progress bulk posting
    achievementProgressBulkPostingMessage: null,
    isAchievementsProgressBulkPosting: false,

    // Achievement progress deleting
    achievementProgressDeletingMessage: null,
    isAchievementsProgressDeleting: false,

    // Locked achievements
    lockedAchievements: [],
    isLockedAchievementsLoading: false,

    // Unlocked achievements
    unlockedAchievements: [],
    isUnlockedAchievementsLoading: false,

    // Completed achievements
    completedAchievements: [],
    isCompletedAchievementsLoading: false,

    // Achievements types
    achievementsTypes: [],
    isAchievementsTypesLoading: false,

    // Not seen achievements
    notSeenAchievements: [],
    isNotSeenAchievementsLoading: false,

    // Achievements timeline
    achievementsTimeline: [],
    isAchievementsTimelineLoading: false,

    // Upload achievement image
    isUploadingAchievementImage: false,
    uploadAchievementImageSuccess: false,
    uploadAchievementImageError: null,
    uploadedAchievementImageData: null,
};

export const reducer = createReducer(defaultState, {
    [ACHIEVEMENTS_REQUEST]: handleRequest,
    [ACHIEVEMENTS_AVAILABLE]: handleAvailable,
    [ACHIEVEMENTS_UNAVAILABLE]: handleUnavailable,
    // Custom handlers for upload image
    UPLOAD_ACHIEVEMENT_IMAGE_REQUEST: (state) => ({
        ...state,
        isUploadingAchievementImage: true,
        uploadAchievementImageSuccess: false,
        uploadAchievementImageError: null,
        uploadedAchievementImageData: null,
    }),
    UPLOAD_ACHIEVEMENT_IMAGE_SUCCESS: (state, { payload }) => ({
        ...state,
        isUploadingAchievementImage: false,
        uploadAchievementImageSuccess: true,
        uploadAchievementImageError: null,
        uploadedAchievementImageData: payload,
    }),
    UPLOAD_ACHIEVEMENT_IMAGE_ERROR: (state, { payload }) => ({
        ...state,
        isUploadingAchievementImage: false,
        uploadAchievementImageSuccess: false,
        uploadAchievementImageError: payload,
        uploadedAchievementImageData: null,
    }),
});

// Action creators for all achievements
export function getAllAchievements() {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'allAchievements';
        const loadingName = 'isAllAchievementsLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            let response = await dataSource.getAllAchievements();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

// Action creators for achievement general progress
export function getAchievementsProgress() {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'achievementsProgress';
        const loadingName = 'isAchievementsProgressLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            let response = await dataSource.getAchievementsProgress();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: {},
                },
            });
        }
    };
}

// Action creators for achievement progress
export function postAchievementsProgress(payload) {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'achievementProgressPostingMessage';
        const loadingName = 'isAchievementsProgressPosting';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            let response = await dataSource.postAchievementsProgress(payload);
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
            const snack = {
                open: true,
                message: 'Achievement progress inserted successfully',
                severity: 'success',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
            const snack = {
                open: true,
                message: 'There was an error creating the achievement to the selected user',
                severity: 'error',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        }
    };
}

export function postAchievementsProgressBulk(payload) {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'achievementProgressBulkPostingMessage';
        const loadingName = 'isAchievementsProgressBulkPosting';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            let response = await dataSource.postAchievementsProgressBulk(payload);
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
            const snack = {
                open: true,
                message: 'Achievement progress bulk inserted successfully',
                severity: 'success',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
            dispatch(getAchievementsProgress());
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
            const snack = {
                open: true,
                message: 'There was an error creating the achievement for the selected users',
                severity: 'error',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        }
    };
}

export function patchAchievementsProgress(payload) {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'achievementProgressPatchingMessage';
        const loadingName = 'isAchievementsProgressPatching';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            await dataSource.patchAchievementsProgress(payload);
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: 'Achievement progress updated successfully',
                    loadingName: loadingName,
                },
            });
            const snack = {
                open: true,
                message: 'Achievement progress updated successfully',
                severity: 'success',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        } catch (error) {
            console.log('error: ', error);
            const snack = {
                open: true,
                message: 'There was an error updating the achievement progress',
                severity: 'error',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

export function deleteAchievementsProgress(achievement_progress_id) {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'achievementProgressDeletingMessage';
        const loadingName = 'isAchievementsProgressDeleting';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName, additionalStates: { achievementProgressDeletingMessage: null } },
        });
        try {
            await dataSource.deleteAchievementsProgress(achievement_progress_id);
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: 'Achievement progress deleted successfully',
                    loadingName: loadingName,
                },
            });
            const snack = {
                open: true,
                message: 'Achievement progress deleted successfully',
                severity: 'success',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
            const snack = {
                open: true,
                message: 'There was an error deleting the achievement progress',
                severity: 'error',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        }
    };
}

// Action creators for locked achievements
export function getLockedAchievements() {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'lockedAchievements';
        const loadingName = 'isLockedAchievementsLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            let response = await dataSource.getLockedAchievements();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

// Action creators for unlocked achievements
export function getUnlockedAchievements() {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'unlockedAchievements';
        const loadingName = 'isUnlockedAchievementsLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            let response = await dataSource.getUnlockedAchievements();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

// Action creators for achievement management
export function postAchievement(achievement) {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'allAchievements';
        const loadingName = 'isAllAchievementsLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            await dataSource.postAchievement(achievement);
            // Reload achievements after successful creation
            let response = await dataSource.getAllAchievements();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
            const snack = {
                open: true,
                message: 'Achievement created successfully',
                severity: 'success',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        } catch (error) {
            console.log('error: ', error);
            const snack = {
                open: true,
                message: 'There was an error creating the achievement',
                severity: 'error',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

export function updateAchievement(achievement) {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'allAchievements';
        const loadingName = 'isAllAchievementsLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            await dataSource.updateAchievement(achievement);
            // Reload achievements after successful update
            let response = await dataSource.getAllAchievements();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
            const snack = {
                open: true,
                message: 'Achievement updated successfully',
                severity: 'success',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        } catch (error) {
            console.log('error: ', error);
            const snack = {
                open: true,
                message: 'There was an error updating the achievement',
                severity: 'error',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

export function deleteAchievement(achievementId) {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'allAchievements';
        const loadingName = 'isAllAchievementsLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName, additionalStates: { achievementProgressPostingMessage: null } },
        });
        try {
            await dataSource.deleteAchievement(achievementId);
            // Reload achievements after successful deletion
            let response = await dataSource.getAllAchievements();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
            const snack = {
                open: true,
                message: 'Achievement deleted successfully',
                severity: 'success',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        } catch (error) {
            console.log('error: ', error);
            const snack = {
                open: true,
                message: 'There was an error deleting the achievement',
                severity: 'error',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

export function uploadAchievementImage(formData) {
    return async (dispatch, _, { services: { dataSource } }) => {
        dispatch({ type: 'UPLOAD_ACHIEVEMENT_IMAGE_REQUEST' });
        try {
            const response = await dataSource.uploadAchievementImage(formData);
            dispatch({ type: 'UPLOAD_ACHIEVEMENT_IMAGE_SUCCESS', payload: response });
            const snack = {
                open: true,
                message: 'Image uploaded successfully',
                severity: 'success',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        } catch (error) {
            dispatch({ type: 'UPLOAD_ACHIEVEMENT_IMAGE_ERROR', payload: error });
            const snack = {
                open: true,
                message: 'There was an error uploading the image',
                severity: 'error',
            };
            dispatch({ type: SNACK_SET, payload: { snack } });
        }
    };
}

export function getAchievementsTypes() {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'achievementsTypes';
        const loadingName = 'isAchievementsTypesLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            const response = await dataSource.getAchievementsTypes();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

export function getNotSeenAchievements() {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'notSeenAchievements';
        const loadingName = 'isNotSeenAchievementsLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            const response = await dataSource.getNotSeenAchievements();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

export function patchNotSeenAchievements(payload) {
    return async (dispatch, _, { services: { dataSource } }) => {
        const loadingName = 'isNotSeenAchievementsLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            const response = await dataSource.patchNotSeenAchievements(payload);
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}

export function getAchievementsTimeline() {
    return async (dispatch, _, { services: { dataSource } }) => {
        const keyState = 'achievementsTimeline';
        const loadingName = 'isAchievementsTimelineLoading';
        dispatch({
            type: ACHIEVEMENTS_REQUEST,
            payload: { loadingName: loadingName },
        });
        try {
            const response = await dataSource.getAchievementsTimeline();
            dispatch({
                type: ACHIEVEMENTS_AVAILABLE,
                payload: {
                    keyState: keyState,
                    data: response,
                    loadingName: loadingName,
                },
            });
        } catch (error) {
            console.log('error: ', error);
            dispatch({
                type: ACHIEVEMENTS_UNAVAILABLE,
                payload: {
                    keyState: keyState,
                    loadingName: loadingName,
                    defaultState: [],
                },
            });
        }
    };
}
